// 절차 노이즈 텍스처(외부 이미지를 받지 않는다). 타일링되는 값 노이즈 fbm 4채널을 생성 때 한 번 계산해 DataTexture로 올린다.
// 아크의 UV 스크롤·디졸브, 링의 끊김, 트레일 줄무늬, 균열 선이 모두 이 텍스처 하나를 다른 좌표로 읽는다.
import * as THREE from 'three/webgpu';

export const NOISE_SIZE = 128;

function hash(ix: number, iy: number, seed: number): number {
  let h = Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263) ^ Math.imul(seed, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** period칸 격자로 타일링되는 값 노이즈(x,y는 0..period) */
function valueNoise(x: number, y: number, period: number, seed: number): number {
  const x0 = Math.floor(x), y0 = Math.floor(y);
  const fx = x - x0, fy = y - y0;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const xa = ((x0 % period) + period) % period, ya = ((y0 % period) + period) % period;
  const xb = (xa + 1) % period, yb = (ya + 1) % period;
  const a = hash(xa, ya, seed), b = hash(xb, ya, seed), c = hash(xa, yb, seed), d = hash(xb, yb, seed);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

/** RGBA8 fbm 노이즈(채널마다 씨앗이 다르다). 4옥타브: 격자 4·8·16·32칸이라 128px에서 정확히 타일링된다 */
export function buildNoiseData(size = NOISE_SIZE): Uint8Array {
  const out = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const o = (y * size + x) * 4;
      for (let c = 0; c < 4; c++) {
        let sum = 0, amp = 0.5, norm = 0;
        for (let oct = 0; oct < 4; oct++) {
          const period = 4 << oct;
          sum += amp * valueNoise((x / size) * period, (y / size) * period, period, 11 + c * 7 + oct);
          norm += amp;
          amp *= 0.5;
        }
        out[o + c] = Math.max(0, Math.min(255, Math.round((sum / norm) * 255)));
      }
    }
  }
  return out;
}

/** 타일링하지 않는 fbm(0..1). 플립북 시트 같은 CPU 생성용: 좌표는 ±100 안에서 쓴다 */
export function fbm(x: number, y: number, seed: number, octaves = 4): number {
  let sum = 0, amp = 0.5, norm = 0, f = 1;
  for (let o = 0; o < octaves; o++) {
    sum += amp * valueNoise(x * f, y * f, 1024, seed + o * 5);
    norm += amp;
    amp *= 0.5;
    f *= 2;
  }
  return sum / norm;
}

/** 모듈이 공유하는 노이즈 텍스처. 사용하는 쪽(ModuleFx)이 소유하고 dispose한다 */
export function createNoiseTexture(): THREE.DataTexture {
  const t = new THREE.DataTexture(buildNoiseData(), NOISE_SIZE, NOISE_SIZE, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearFilter;
  t.generateMipmaps = false;
  t.colorSpace = THREE.NoColorSpace;
  t.needsUpdate = true;
  return t;
}
